import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useTheme } from '../../contexts/ThemeContext.jsx';

const AdminAnalytics = () => {
  const { theme } = useTheme();
  const { getToken } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('7_days');

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getToken();
      const response = await fetch(`https://parrotmate.com/api/admin/analytics/live?timeRange=${timeRange}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      } else {
        throw new Error('Failed to fetch analytics data');
      }
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const MetricCard = ({ title, value, icon, trend, trendValue, isLoading }) => (
    <div className={`${
      theme === 'dark' 
        ? 'bg-gray-800 border-gray-700' 
        : 'bg-white border-gray-200'
    } rounded-lg shadow border p-6`}>
      <div className="flex items-center justify-between">
        <div>
          <p className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            {title}
          </p>
          <div className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            {isLoading ? (
              <div className={`animate-pulse h-8 w-16 rounded ${theme === 'dark' ? 'bg-gray-600' : 'bg-gray-300'}`}></div>
            ) : (
              value
            )}
          </div>
          {trend && trendValue && !isLoading && (
            <p className={`text-sm ${trend === 'up' ? 'text-green-600' : trend === 'down' ? 'text-red-600' : 'text-gray-500'} flex items-center mt-1`}>
              <span className="mr-1">
                {trend === 'up' ? '↗' : trend === 'down' ? '↘' : '→'}
              </span>
              {trendValue}
            </p>
          )}
        </div>
        <div className={`text-3xl ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`}>
          {icon}
        </div>
      </div>
    </div>
  );

  const UserGrowthChart = () => (
    <div className={`${
      theme === 'dark' 
        ? 'bg-gray-800 border-gray-700' 
        : 'bg-white border-gray-200'
    } rounded-lg shadow border p-6`}>
      <h3 className={`text-lg font-medium mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
        User Growth
      </h3>
      {loading ? (
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
        </div>
      ) : analytics?.trends?.userGrowth?.length > 0 ? (
        <div className="h-64 overflow-x-auto">
          <div className="flex items-end justify-between h-full space-x-2 min-w-full">
            {analytics.trends.userGrowth.slice(-14).map((data, index) => {
              const maxUsers = Math.max(...analytics.trends.userGrowth.map(d => d.totalUsers));
              const height = maxUsers > 0 ? (data.totalUsers / maxUsers) * 200 : 0;
              return (
                <div key={index} className="flex flex-col items-center flex-1 min-w-0">
                  <div className="flex flex-col items-center justify-end h-48 w-full">
                    <div 
                      className="bg-teal-500 rounded-t w-full min-h-1 transition-all duration-300"
                      style={{ height: `${height}px` }}
                      title={`${data.totalUsers} users on ${new Date(data.date).toLocaleDateString()}`}
                    ></div>
                  </div>
                  <div className={`text-xs mt-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} truncate w-full text-center`}>
                    {new Date(data.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </div>
                  <div className={`text-xs ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} font-medium`}>
                    {data.totalUsers}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="h-64 flex items-center justify-center">
          <div className="text-center">
            <div className={`text-4xl mb-2 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`}>📈</div>
            <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              No user growth data available
            </p>
          </div>
        </div>
      )}
    </div>
  );

  const TopPerformingParrots = () => (
    <div className={`${
      theme === 'dark' 
        ? 'bg-gray-800 border-gray-700' 
        : 'bg-white border-gray-200'
    } rounded-lg shadow border p-6`}>
      <h3 className={`text-lg font-medium mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
        Top Performing Parrots
      </h3>
      {loading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-center space-x-3">
              <div className={`animate-pulse h-4 w-4 rounded ${theme === 'dark' ? 'bg-gray-600' : 'bg-gray-300'}`}></div>
              <div className={`animate-pulse h-4 flex-1 rounded ${theme === 'dark' ? 'bg-gray-600' : 'bg-gray-300'}`}></div>
              <div className={`animate-pulse h-4 w-16 rounded ${theme === 'dark' ? 'bg-gray-600' : 'bg-gray-300'}`}></div>
            </div>
          ))}
        </div>
      ) : analytics?.topPerformingParrots?.length > 0 ? (
        <div className="space-y-3">
          {analytics.topPerformingParrots.slice(0, 10).map((parrot, index) => (
            <div key={parrot.parrotId} className="flex items-center justify-between">
              <div className="flex items-center space-x-3 flex-1 min-w-0">
                <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                  index === 0 ? 'bg-yellow-500 text-white' :
                  index === 1 ? 'bg-gray-400 text-white' :
                  index === 2 ? 'bg-orange-600 text-white' :
                  theme === 'dark' ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-700'
                }`}>
                  {index + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium truncate ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {parrot.parrotName}
                  </p>
                  <p className={`text-xs truncate ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    {parrot.userName}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-4 flex-shrink-0">
                <div className="text-right">
                  <p className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {parrot.calls} calls
                  </p>
                  <p className={`text-xs ${parrot.successRate >= 90 ? 'text-green-600' : parrot.successRate >= 70 ? 'text-yellow-600' : 'text-red-600'}`}>
                    {parrot.successRate}% success
                  </p>
                </div>
                <div className="text-right">
                  <p className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    ${parrot.revenue.toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="h-48 flex items-center justify-center">
          <div className="text-center">
            <div className={`text-4xl mb-2 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`}>🦜</div>
            <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              No parrot performance data available
            </p>
          </div>
        </div>
      )}
    </div>
  );

  const CallVolumeChart = () => (
    <div className={`${
      theme === 'dark' 
        ? 'bg-gray-800 border-gray-700' 
        : 'bg-white border-gray-200'
    } rounded-lg shadow border p-6`}>
      <h3 className={`text-lg font-medium mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
        Call Volume Over Time
      </h3>
      {loading ? (
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
        </div>
      ) : analytics?.trends?.callVolume?.length > 0 ? (
        <div className="h-64 overflow-x-auto">
          <div className="flex items-end justify-between h-full space-x-2 min-w-full">
            {analytics.trends.callVolume.slice(-14).map((data, index) => {
              const maxCalls = Math.max(...analytics.trends.callVolume.map(d => d.calls));
              const height = maxCalls > 0 ? (data.calls / maxCalls) * 200 : 0;
              const successHeight = maxCalls > 0 ? (data.successfulCalls / maxCalls) * 200 : 0;
              return (
                <div key={index} className="flex flex-col items-center flex-1 min-w-0">
                  <div className="flex flex-col items-center justify-end h-48 w-full">
                    <div className="relative w-full">
                      <div 
                        className="bg-red-300 rounded-t w-full min-h-1"
                        style={{ height: `${height}px` }}
                        title={`${data.calls} total calls on ${new Date(data.date).toLocaleDateString()}`}
                      ></div>
                      <div 
                        className="bg-teal-500 rounded-t w-full absolute bottom-0"
                        style={{ height: `${successHeight}px` }}
                        title={`${data.successfulCalls} successful calls on ${new Date(data.date).toLocaleDateString()}`}
                      ></div>
                    </div>
                  </div>
                  <div className={`text-xs mt-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} truncate w-full text-center`}>
                    {new Date(data.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </div>
                  <div className={`text-xs ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} font-medium`}>
                    {data.calls}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-center mt-4 space-x-4">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-teal-500 rounded"></div>
              <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Successful</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-red-300 rounded"></div>
              <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Total</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="h-64 flex items-center justify-center">
          <div className="text-center">
            <div className={`text-4xl mb-2 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`}>📞</div>
            <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              No call volume data available
            </p>
          </div>
        </div>
      )}
    </div>
  );

  const RevenueTrendsChart = () => (
    <div className={`${
      theme === 'dark' 
        ? 'bg-gray-800 border-gray-700' 
        : 'bg-white border-gray-200'
    } rounded-lg shadow border p-6`}>
      <h3 className={`text-lg font-medium mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
        Revenue Trends
      </h3>
      {loading ? (
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
        </div>
      ) : analytics?.trends?.revenue?.length > 0 ? (
        <div className="h-64 overflow-x-auto">
          <div className="flex items-end justify-between h-full space-x-2 min-w-full">
            {analytics.trends.revenue.slice(-14).map((data, index) => {
              const maxRevenue = Math.max(...analytics.trends.revenue.map(d => d.revenue));
              const height = maxRevenue > 0 ? (data.revenue / maxRevenue) * 200 : 0;
              return (
                <div key={index} className="flex flex-col items-center flex-1 min-w-0">
                  <div className="flex flex-col items-center justify-end h-48 w-full">
                    <div 
                      className="bg-green-500 rounded-t w-full min-h-1 transition-all duration-300"
                      style={{ height: `${height}px` }}
                      title={`${data.revenue.toFixed(2)} revenue on ${new Date(data.date).toLocaleDateString()}`}
                    ></div>
                  </div>
                  <div className={`text-xs mt-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} truncate w-full text-center`}>
                    {new Date(data.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </div>
                  <div className={`text-xs ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} font-medium`}>
                    ${data.revenue.toFixed(0)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="h-64 flex items-center justify-center">
          <div className="text-center">
            <div className={`text-4xl mb-2 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`}>💰</div>
            <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              No revenue data available
            </p>
          </div>
        </div>
      )}
    </div>
  );

  const PlatformPerformanceMetrics = () => (
    <div className={`${
      theme === 'dark' 
        ? 'bg-gray-800 border-gray-700' 
        : 'bg-white border-gray-200'
    } rounded-lg shadow border`}>
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
          Platform Performance Metrics
        </h3>
      </div>
      <div className="p-6">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="space-y-2">
                <div className={`animate-pulse h-4 w-24 rounded ${theme === 'dark' ? 'bg-gray-600' : 'bg-gray-300'}`}></div>
                <div className={`animate-pulse h-8 w-16 rounded ${theme === 'dark' ? 'bg-gray-600' : 'bg-gray-300'}`}></div>
              </div>
            ))}
          </div>
        ) : analytics ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <p className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-2`}>
                Overall Success Rate
              </p>
              <div className={`text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-1`}>
                {analytics.platformMetrics.totalCalls > 0 
                  ? Math.round((analytics.platformMetrics.totalCalls / analytics.platformMetrics.totalCalls) * 100) 
                  : 0}%
              </div>
              <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Platform-wide call success
              </p>
            </div>
            
            <div className="text-center">
              <p className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-2`}>
                Average Revenue per User
              </p>
              <div className={`text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-1`}>
                ${analytics.platformMetrics.activeUsers > 0 
                  ? (analytics.platformMetrics.totalRevenue / analytics.platformMetrics.activeUsers).toFixed(2)
                  : '0.00'}
              </div>
              <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                ARPU for active users
              </p>
            </div>
            
            <div className="text-center">
              <p className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-2`}>
                Calls per Active User
              </p>
              <div className={`text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-1`}>
                {analytics.platformMetrics.activeUsers > 0 
                  ? Math.round(analytics.platformMetrics.totalCalls / analytics.platformMetrics.activeUsers)
                  : 0}
              </div>
              <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Average usage per user
              </p>
            </div>
            
            <div className="text-center">
              <p className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-2`}>
                User Activation Rate
              </p>
              <div className={`text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-1`}>
                {analytics.platformMetrics.totalUsers > 0 
                  ? Math.round((analytics.platformMetrics.activeUsers / analytics.platformMetrics.totalUsers) * 100)
                  : 0}%
              </div>
              <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Users with calls
              </p>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <div className={`text-4xl mb-2 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`}>⚡</div>
            <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              No performance data available
            </p>
          </div>
        )}
      </div>
    </div>
  );

  const handleExportAnalytics = async () => {
    if (!analytics) return;
    
    try {
      const exportData = {
        exportDate: new Date().toISOString(),
        timeRange: analytics.timeRange,
        platformMetrics: analytics.platformMetrics,
        topPerformingParrots: analytics.topPerformingParrots,
        trends: analytics.trends
      };

      // Create and download CSV
      const csvContent = generateCSV(exportData);
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `parrotmate-analytics-${analytics.timeRange.type}-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Failed to export analytics:', error);
      alert('Failed to export analytics data');
    }
  };

  const generateCSV = (data) => {
    const lines = [];
    
    // Header
    lines.push('ParrotMate Analytics Export');
    lines.push(`Export Date: ${new Date(data.exportDate).toLocaleString()}`);
    lines.push(`Time Range: ${data.timeRange.type} (${data.timeRange.start} to ${data.timeRange.end})`);
    lines.push('');
    
    // Platform Metrics
    lines.push('Platform Metrics');
    lines.push('Metric,Value');
    lines.push(`Total Users,${data.platformMetrics.totalUsers}`);
    lines.push(`Active Users,${data.platformMetrics.activeUsers}`);
    lines.push(`Total Calls,${data.platformMetrics.totalCalls}`);
    lines.push(`Total Revenue,${data.platformMetrics.totalRevenue}`);
    lines.push(`Active Parrots,${data.platformMetrics.activeParrots}`);
    lines.push(`Calls Today,${data.platformMetrics.callsToday}`);
    lines.push(`Revenue This Month,${data.platformMetrics.revenueThisMonth}`);
    lines.push(`User Growth,${data.platformMetrics.userGrowth.percentChange}%`);
    lines.push(`Call Volume Growth,${data.platformMetrics.callVolumeGrowth.percentChange}%`);
    lines.push('');
    
    // Top Performing Parrots
    lines.push('Top Performing Parrots');
    lines.push('Parrot Name,User Name,Calls,Success Rate,Revenue');
    data.topPerformingParrots.forEach(parrot => {
      lines.push(`"${parrot.parrotName}","${parrot.userName}",${parrot.calls},${parrot.successRate}%,${parrot.revenue}`);
    });
    lines.push('');
    
    // User Growth Trend
    if (data.trends.userGrowth.length > 0) {
      lines.push('User Growth Trend');
      lines.push('Date,New Users,Total Users');
      data.trends.userGrowth.forEach(trend => {
        lines.push(`${trend.date},${trend.newUsers},${trend.totalUsers}`);
      });
      lines.push('');
    }
    
    // Call Volume Trend
    if (data.trends.callVolume.length > 0) {
      lines.push('Call Volume Trend');
      lines.push('Date,Total Calls,Successful Calls');
      data.trends.callVolume.forEach(trend => {
        lines.push(`${trend.date},${trend.calls},${trend.successfulCalls}`);
      });
      lines.push('');
    }
    
    // Revenue Trend
    if (data.trends.revenue.length > 0) {
      lines.push('Revenue Trend');
      lines.push('Date,Revenue');
      data.trends.revenue.forEach(trend => {
        lines.push(`${trend.date},${trend.revenue}`);
      });
    }
    
    return lines.join('\n');
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            Analytics Dashboard
          </h2>
          <p className={`mt-1 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Platform usage statistics and insights
          </p>
        </div>
        
        {/* Time Range Selector */}
        <div className="flex items-center space-x-2">
          <label className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
            Time Range:
          </label>
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className={`px-3 py-2 rounded-lg border transition-colors ${
              theme === 'dark'
                ? 'bg-gray-700 border-gray-600 text-white focus:ring-teal-500 focus:border-teal-500'
                : 'bg-white border-gray-300 text-gray-900 focus:ring-teal-500 focus:border-teal-500'
            }`}
          >
            <option value="1_day">Last 24 Hours</option>
            <option value="7_days">Last 7 Days</option>
            <option value="30_days">Last 30 Days</option>
            <option value="60_days">Last 60 Days</option>
            <option value="90_days">Last 90 Days</option>
            <option value="month_to_date">Month to Date</option>
          </select>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className={`${
          theme === 'dark' ? 'bg-red-900 border-red-700 text-red-200' : 'bg-red-50 border-red-200 text-red-700'
        } border rounded-lg p-4 mb-6`}>
          <div className="flex items-center">
            <span className="text-xl mr-2">⚠️</span>
            <div>
              <p className="font-medium">Failed to load analytics data</p>
              <p className="text-sm mt-1">{error}</p>
              <button
                onClick={fetchAnalytics}
                className="text-sm underline mt-2 hover:no-underline"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Metric Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <MetricCard
          title="Total Users"
          value={analytics?.platformMetrics?.totalUsers?.toLocaleString() || '0'}
          icon="👥"
          trend={analytics?.platformMetrics?.userGrowth?.percentChange > 0 ? 'up' : analytics?.platformMetrics?.userGrowth?.percentChange < 0 ? 'down' : 'neutral'}
          trendValue={analytics?.platformMetrics?.userGrowth?.percentChange ? `${analytics.platformMetrics.userGrowth.percentChange > 0 ? '+' : ''}${analytics.platformMetrics.userGrowth.percentChange}% from last period` : null}
          isLoading={loading}
        />
        <MetricCard
          title="Total Calls"
          value={analytics?.platformMetrics?.totalCalls?.toLocaleString() || '0'}
          icon="📞"
          trend={analytics?.platformMetrics?.callVolumeGrowth?.percentChange > 0 ? 'up' : analytics?.platformMetrics?.callVolumeGrowth?.percentChange < 0 ? 'down' : 'neutral'}
          trendValue={analytics?.platformMetrics?.callVolumeGrowth?.percentChange ? `${analytics.platformMetrics.callVolumeGrowth.percentChange > 0 ? '+' : ''}${analytics.platformMetrics.callVolumeGrowth.percentChange}% from last period` : null}
          isLoading={loading}
        />
        <MetricCard
          title="Total Revenue"
          value={analytics?.platformMetrics?.totalRevenue ? `${analytics.platformMetrics.totalRevenue.toLocaleString()}` : '$0'}
          icon="💰"
          isLoading={loading}
        />
        <MetricCard
          title="Active Parrots"
          value={analytics?.platformMetrics?.activeParrots?.toLocaleString() || '0'}
          icon="🦜"
          isLoading={loading}
        />
        <MetricCard
          title="Calls Today"
          value={analytics?.platformMetrics?.callsToday?.toLocaleString() || '0'}
          icon="📊"
          isLoading={loading}
        />
        <MetricCard
          title="Revenue This Month"
          value={analytics?.platformMetrics?.revenueThisMonth ? `${analytics.platformMetrics.revenueThisMonth.toLocaleString()}` : '$0'}
          icon="📈"
          isLoading={loading}
        />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CallVolumeChart />
        <RevenueTrendsChart />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <UserGrowthChart />
        <TopPerformingParrots />
      </div>

      {/* Platform Performance Metrics */}
      <div className="space-y-6">
        <PlatformPerformanceMetrics />
        
        {/* Analytics Export Section */}
        <div className={`${
          theme === 'dark' 
            ? 'bg-gray-800 border-gray-700' 
            : 'bg-white border-gray-200'
        } rounded-lg shadow border`}>
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center">
              <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Analytics Export
              </h3>
              <button
                onClick={handleExportAnalytics}
                disabled={loading}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 transition-colors focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 dark:text-white"
              >
                {loading ? 'Exporting...' : 'Export Data'}
              </button>
            </div>
          </div>
          <div className="p-6">
            <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
              Export comprehensive analytics data including user metrics, call statistics, revenue trends, and platform performance data for the selected time period.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAnalytics;
